import { type GridRowId } from "@mui/x-data-grid-pro";
import type {
  AcquireBookPictures,
  AcquireBookZenbujiko,
} from "@/types/acquirebook";
import { applyZenbujikoGenzaiTransform } from "@/utils/utilsAcquireBook";
import type { BulkAcquisitionItem, BulkPropertyRow } from "../types";

/**
 * チェック行（絞り込みで非表示の行を含む）から一括依頼の items[] を組み立てる。
 * チェックボックス→pictures/zenbujikoOptions の変換規則は
 * 既存 acquireMultipleBooks の buildBasePictureRequest と同一。
 * pictures が空になる行（取得対象のチェックがない行）は依頼に含めない
 */
export const buildBulkAcquisitionItems = (
  rows: BulkPropertyRow[],
  checkedIds: Record<string, Set<GridRowId>>
): BulkAcquisitionItem[] => {
  const isChecked = (field: string, id: GridRowId): boolean =>
    checkedIds[field]?.has(id) ?? false;

  const items: BulkAcquisitionItem[] = [];
  for (const row of rows) {
    const pictures: AcquireBookPictures[] = [];
    let zenbujikoOptions: AcquireBookZenbujiko[] = [];

    if (isChecked("ownerInfo", row.id)) {
      pictures.push("SHOYUSHAJIKO");
    }
    if (isChecked("sintaku", row.id)) {
      zenbujikoOptions.push("SHINTAKUMOKUROKU");
    }
    if (isChecked("kyoutan", row.id)) {
      zenbujikoOptions.push("KYODOTANPOMOKUROKU");
    }
    if (isChecked("requestClosureBook", row.id)) {
      zenbujikoOptions.push("HEISATOUKIBO");
    }
    if (isChecked("tisekiDrawings", row.id)) {
      pictures.push("CHISEKISOKURYOZU");
    }
    if (isChecked("electricDrawings", row.id)) {
      pictures.push("DENSHIKOZU");
    }
    if (isChecked("tiekiDrawings", row.id)) {
      pictures.push("CHIEKIKENZUMEN");
    }
    if (isChecked("buildingDrawings", row.id)) {
      pictures.push("TATEMONOZUMEN");
    }

    if (
      isChecked("sintaku", row.id) ||
      isChecked("kyoutan", row.id) ||
      isChecked("requestClosureBook", row.id)
    ) {
      pictures.push("ZENBUJIKO");
    }

    if (isChecked("genzai", row.id)) {
      zenbujikoOptions = applyZenbujikoGenzaiTransform(zenbujikoOptions);
    }

    if (pictures.length === 0) {
      continue;
    }
    items.push({
      propertyCode: row.propertyCode,
      // 照合APIが返した連結済み値（「A社、B社」）をそのまま往復する
      companyName: row.companyName,
      pictures,
      zenbujikoOptions,
    });
  }
  return items;
};

/**
 * 依頼に実際に載る書類のうち、絞り込みで非表示になっている行のぶんを数える。
 * 取得方法の選別（取得済のみ・リユースのみ）で落ちた書類は依頼に載らないため、
 * チェック数ではなく選別後の items から数える。
 * items は物件コード、可視判定は行IDのため rowsByPropertyCode 経由で引き当てる
 * @param visibleRowIds 表示中の行ID。null は全行表示（読み込み前含む）
 */
export const countHiddenPictures = (
  items: BulkAcquisitionItem[],
  rowsByPropertyCode: Map<string, BulkPropertyRow>,
  visibleRowIds: Set<GridRowId> | null
): number => {
  if (visibleRowIds == null) {
    return 0;
  }
  return items.reduce((sum, item) => {
    const row = rowsByPropertyCode.get(item.propertyCode);
    return row == null || visibleRowIds.has(row.id)
      ? sum
      : sum + item.pictures.length;
  }, 0);
};
